import { inject } from '@angular/core';

import { type IoConfig, type PartialPropsOf, type PropsOf } from '@blaze-runtime/engine-domain';
import {
  type ChartVariantType,
  getActiveBinding,
  type PropertiesConfig,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { findColumn } from '@blaze-shared/schema-domain';
import { type DatasetColumn, DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';
import { getValueByPath, isAggregateNode, isComputeNode } from '@blaze-shared/workflow-domain';

import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import {
  AGGREGATION_OPTIONS,
  CHART_DIMENSIONS,
  type ChartPropsModel,
  DATE_FORMATS_OPTIONS,
  DATE_PERIOD_OPTIONS,
  MONTH_FORMATS_OPTIONS,
  NUMBER_FIELD_FORMATS,
  OPTIONS_PICKER_ORDER,
  TIME_FORMATS_OPTIONS,
} from './chart.models';
import { isComputeNodeForChart } from './utils/chart-data.utils';
import { onlyDateWithMultipleLevels } from './utils/chart-select-options.utils';

//#region Common builders

export function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      { eventType: 'select', schema: DEFAULT_SCHEMA }, // overriden inside chart.component
    ],
  };
}

export function getPropsConfig(
  type: 'chartLine' | 'chartBar' | 'chartColumn' | 'chartDot',
): PropertiesConfig {
  return epc.initConfig(type, [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'data.?key:value.enabled',
        '',
        [
          epc.fields.selectDataSourceField({
            key: 'value',
            label: 'Data Source',
            sources: 'list',
            bindings: [],
          }),
        ],
        {
          labelWidth: '40%',
          hideLabel: true,
        },
      ),
      epc.subsections.subsection(
        'data.?key:value.enabled',
        'X Axis',
        [
          epc.utils.observedField(
            epc.fields.selectDataValueField(
              'X Axis',
              ['content.data.groupBy', 'content.data.groupByDetails'],
              [DATE_PERIOD_OPTIONS, 'data.?key:value.dataSource'],
              {
                dataBindingsMode: 'first-dataset',
                allowSubSelection: true,
                emptyListPlaceholder: 'No fields',
                backBtnLabel: 'Back',
                allowOpenStepsPredicate: onlyDateWithMultipleLevels,
                preventSelectingValueFromPaths: ['content.data.mainValue'],
                reverseOrder: true,
                allowOpenStepsByTypePredicate: true,
                subSelectionForColumnType: ['Date', 'DateTime'],
                nodePredicate: isComputeNodeForChart,
                alternativeNodePredicate: isAggregateNode,
                hideLabel: true,
                placeholder: 'Select',
              },
            ),
            (model: ChartPropsModel, value: string) => {
              const result: Record<string, any> = {};
              //if monthly
              if (model.content.data?.groupByDetails === 'monthly') {
                if (!model.content.format?.dateXFormat) {
                  result['content.format.dateXFormat'] = 'number-month-first-year';
                }
                switch (model.content.format?.dateXFormat) {
                  case 'number-month-first':
                  case 'number-day-first':
                    result['content.format.dateXFormat'] = 'number-month-first-year';
                    break;
                  case 'short-month':
                    result['content.format.dateXFormat'] = 'short-month-year';
                    break;
                  case 'long-month':
                    result['content.format.dateXFormat'] = 'long-month-year';
                }
              } else if (model.content.data?.groupByDetails) {
                if (!model.content.format?.dateXFormat) {
                  result['content.format.dateXFormat'] = 'number-month-first';
                }
                switch (model.content.format?.dateXFormat) {
                  case 'number-month-first-year':
                    result['content.format.dateXFormat'] = 'number-month-first';
                    break;
                  case 'short-month-year':
                    result['content.format.dateXFormat'] = 'short-month';
                    break;
                  case 'long-month-year':
                    result['content.format.dateXFormat'] = 'long-month';
                }
              } else if (isGroupByTime(model, value)) {
                result['content.format.dateXFormat'] = 'with-meridiem';
              }
              if (model.content.data?.segmentData === value) {
                result['content.data.segmentData'] = model.content.data?.groupBy || '';
                result['content.data.segmentDataDetails'] =
                  model.content.data?.groupByDetails || '';
                if (type === 'chartDot') {
                  result['content.data.customIcons.iconMap'] = [];
                }
              }
              return result;
            },
          ),
        ],
        {
          hideWhen: (_, __, model: ChartPropsModel) =>
            !getValueByPath(model, 'data.?key:value') ||
            getValueByPath(model, 'content.data.aggregation') === 'FIELD VALUE',
        },
      ),
      epc.subsections.subsection(
        'data.?key:value.enabled',
        'X Axis',
        [
          epc.utils.observedField(
            epc.fields.selectDataValueField(
              'X Axis',
              ['content.data.groupBy', 'content.data.groupByDetails'],
              [DATE_PERIOD_OPTIONS, 'data.?key:value.dataSource'],
              {
                dataBindingsMode: 'first-dataset',
                allowSubSelection: true,
                emptyListPlaceholder: 'No fields',
                backBtnLabel: 'Back',
                allowOpenStepsPredicate: onlyDateWithMultipleLevels,
                preventSelectingValueFromPaths: ['content.data.mainValue'],
                reverseOrder: true,
                allowOpenStepsByTypePredicate: true,
                subSelectionForColumnType: ['Date', 'DateTime'],
                nodePredicate: isComputeNodeForChart,
                alternativeNodePredicate: isAggregateNode,
                hideLabel: true,
                placeholder: 'Select',
              },
            ),
            (model: ChartPropsModel, value: string) => {
              const result: Record<string, any> = {};
              if (model.content.data?.segmentData === value) {
                result['content.data.segmentData'] = model.content.data?.groupBy || '';
                result['content.data.segmentDataDetails'] =
                  model.content.data?.groupByDetails || '';
                if (type === 'chartDot') {
                  result['content.data.customIcons.iconMap'] = [];
                }
              }
              return result;
            },
          ),
        ],
        {
          hideWhen: (_, __, model: ChartPropsModel) =>
            !getValueByPath(model, 'data.?key:value') ||
            getValueByPath(model, 'content.data.aggregation') !== 'FIELD VALUE',
        },
      ),
      epc.subsections.subsection(
        'data.?key:value.enabled',
        'Segment By',
        [
          epc.utils.observedField(
            epc.fields.selectDataValueField(
              'Y Axis',
              ['content.data.segmentData', 'content.data.segmentDataDetails'],
              [DATE_PERIOD_OPTIONS, 'data.?key:value.dataSource'],
              {
                dataBindingsMode: 'first-dataset',
                allowSubSelection: true,
                emptyListPlaceholder: 'No fields',
                backBtnLabel: 'Back',
                allowOpenStepsPredicate: onlyDateWithMultipleLevels,
                preventSelectingValueFromPaths: ['content.data.mainValue'],
                allowNone: true,
                reverseOrder: true,
                subSelectionForColumnType: ['Date', 'DateTime'],
                nodePredicate: isComputeNode,
                alternativeNodePredicate: isAggregateNode,
                hideLabel: true,
                placeholder: 'Select (Optional)',
              },
            ),
            (model: ChartPropsModel, value: string) => {
              const result: Record<string, any> = {};
              if (type === 'chartDot') {
                result['content.data.customIcons.iconMap'] = [];
              }
              return {
                ...result,
                ...(model.content.data?.groupBy === value ? { 'content.data.groupBy': '' } : {}),
                ...(value ? { 'content.data.order': 'x' } : {}),
              };
            },
          ),
          epc.utils.conditionalField(
            epc.fields.toggleField('content.data.customIcons.enabled', 'Use custom icons'),
            'content.data.customIcons',
            () => type !== 'chartDot',
          ),
          ...epc.utils.conditionalFields(
            [epc.fields.listChartCustomIcons('content.data.customIcons.iconMap', '')],
            'content.data.customIcons.enabled',
            (value) => type !== 'chartDot' || !value,
          ),
        ],
        {
          hideWhen: (_, __, model: ChartPropsModel) => !getValueByPath(model, 'data.?key:value'),
        },
      ),
      epc.subsections.subsection(
        'data.?key:value.enabled',
        '',
        [
          epc.fields.listChartFields('content.data.multiSeries', 'Y Axis', AGGREGATION_OPTIONS, {
            hideAddButton: type === 'chartDot',
            preventListAction: type === 'chartDot',
          }),
        ],
        {
          hideLabel: true,
          hideWhen: (_, __, model: ChartPropsModel) => !getValueByPath(model, 'data.?key:value'),
        },
      ),
      epc.subsections.subsection(
        'data.?key:value.enabled',
        'Options',
        [
          type === 'chartLine'
            ? epc.fields.toggleField('content.format.curvedLines', 'Curved lines')
            : undefined,
          epc.fields.selectFieldFromList('content.data.order', 'Order', OPTIONS_PICKER_ORDER, {
            showSelection: true,
            allowNone: false,
            disableWhen: (model) => {
              return (
                (!!getValueByPath(model, 'content.data.segmentData') &&
                  'Disabled when series selected') ||
                undefined
              );
            },
          }),
          epc.fields.contentAlignField('content.data.direction', 'Direction', {
            options: [
              { label: 'A-Z', value: 'Ascending' },
              { label: 'Z-A', value: 'Descending' },
            ],
          }),
          epc.fields.textInput('content.data.minValue', 'Min value', {
            placeholder: 'Auto',
            inputType: 'number',
          }),
          epc.fields.textInput('content.data.maxValue', 'Max value', {
            placeholder: 'Auto',
            inputType: 'number',
          }),
          epc.utils.separatedField(
            epc.fields.selectFieldFromList(
              'content.format.type',
              'Type',
              NUMBER_FIELD_FORMATS.map((v) => ({ label: v.toString(), value: v.toString() })),
              {
                showSelection: true,
                allowNone: false,
              },
            ),
            'Y Axis Format',
          ),
          epc.fields.currencyConditionalField('content.format.currency', 'content.format.type'),
          epc.fields.numberPrecisionField('content.format.precision'),
          ...epc.utils.conditionalFields(
            [
              epc.utils.separator('X Axis Format'),
              epc.fields.selectFieldFromList(
                'content.format.dateXFormat',
                'Time format',
                TIME_FORMATS_OPTIONS,
                {
                  showSelection: true,
                  allowNone: false,
                },
              ),
            ],
            'content.data.groupBy',
            (target, model) => {
              const activeBinding = getActiveBinding(model.data, 'value');
              if (activeBinding?.dataSource?.sourceType === 'context') {
                const schemaService = inject(SchemaService);
                const schema = schemaService.getSchema(activeBinding?.dataSource?.id ?? '', true);
                const column = findColumn(schema?.columns, target);
                return column?.dataType !== 'Time';
              }
              return true;
            },
          ),
          ...epc.utils.conditionalFields(
            [
              epc.utils.separator('X Axis Format'),
              epc.fields.selectFieldFromList(
                'content.format.dateXFormat',
                'Date format',
                DATE_FORMATS_OPTIONS,
                {
                  showSelection: true,
                  allowNone: false,
                },
              ),
            ],
            'content.data.groupByDetails',
            (target) => !['daily', 'weekly'].includes(target ?? ''),
          ),
          ...epc.utils.conditionalFields(
            [
              epc.utils.separator('X Axis Format'),
              epc.fields.selectFieldFromList(
                'content.format.dateXFormat',
                'Date format',
                MONTH_FORMATS_OPTIONS,
                {
                  showSelection: true,
                  allowNone: false,
                },
              ),
            ],
            'content.data.groupByDetails',
            (target) => target !== 'monthly',
          ),
          epc.utils.conditionalField(
            epc.utils.separator('Series Format', { hideWhenNextElementHidden: true }),
            'content.data.segmentDataDetails',
            (target) => !['monthly', 'daily', 'weekly'].includes(target ?? ''),
          ),
          epc.utils.conditionalField(
            epc.fields.selectFieldFromList(
              'content.format.dateSeriesFormat',
              'Date format',
              DATE_FORMATS_OPTIONS,
              {
                showSelection: true,
                allowNone: false,
              },
            ),
            'content.data.segmentDataDetails',
            (target) => !['daily', 'weekly'].includes(target ?? ''),
          ),
          epc.utils.conditionalField(
            epc.fields.selectFieldFromList(
              'content.format.dateSeriesFormat',
              'Date format',
              MONTH_FORMATS_OPTIONS,
              {
                showSelection: true,
                allowNone: false,
              },
            ),
            'content.data.segmentDataDetails',
            (target) => target !== 'monthly',
          ),
        ],
        {
          hideWhen: (_, __, model: ChartPropsModel) => !getValueByPath(model, 'data.?key:value'),
        },
      ),
      epc.subsections.elementSizeSubsection(
        CHART_DIMENSIONS.minSize,
        CHART_DIMENSIONS.maxWidth,
        CHART_DIMENSIONS.maxHeight,
        CHART_DIMENSIONS.widthOptions,
        CHART_DIMENSIONS.heightOptions,
      ),
      epc.subsections.visibilitySubsection(),
      epc.subsections.subsection('content.chart', 'Label', [
        epc.fields.textInput('content.chart.xDescription', 'X Axis'),
        epc.fields.textInput('content.chart.yDescription', 'Y Axis'),
      ]),

      epc.subsections.titleContentSubsection('content', [
        epc.fields.textInput('content.title.description', 'Description'),
      ]),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Padding', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),
        epc.subsections.subsection('style.chart', 'Chart colors', [
          epc.fields.colorField('style.chart.gridLines', 'Grid lines', {
            allowGradient: false,
            allowSolid: true,
          }),
          ...(type === 'chartDot'
            ? [
                epc.fields.selectFieldFromList(
                  'style.chart.gridDensity',
                  'Grid density',
                  [
                    { value: 'regular', label: 'Regular' },
                    { value: 'dense', label: 'Dense' },
                  ],
                  { allowNone: false },
                ),
              ]
            : []),
          epc.fields.colorField(`style.general.axis`, 'Axis', {
            allowGradient: false,
            allowNone: false,
          }),
          ...(type === 'chartDot'
            ? [
                epc.fields.sliderField('style.chart.iconSize', 'Icon size', [
                  { min: 0, max: 25, unit: 'px' },
                ]),
              ]
            : []),
          epc.fields.tagsColorPaletteField('style.chart.palette', 'Series', {
            bindingKey: 'optionsList',
            hideSelector: true,
            hideForeColor: true,
            editablePalette: true,
            palettes: [{ label: 'Palette 2', value: 'palette2' }],
          }),
        ]),
        epc.subsections.subsection('style.background', 'Background', [
          epc.fields.colorField('style.background.fill', 'Fill', { allowNone: false }),
          epc.fields.colorField('style.background.outline', 'Outline', {
            allowGradient: false,
            allowSolid: true,
            hasOutlineSides: true,
          }),
          epc.fields.sliderField('style.background.width', 'Width', [
            { min: 0, max: 25, unit: 'px' },
          ]),
          epc.fields.radiusField('style.background.radius'),
          ...epc.fields.visualEffectField('style.background.visualEffect'),
        ]),

        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.general.title',
          descriptionColor: 'style.general.description',
          fontFamily: 'content.font.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'content.font.style',
          fontSize: 'content.font.fontSize',
          horizontalAlign: 'style.general.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}

export function getDefaults(title: string): PropsOf<Exclude<ChartVariantType, 'chartSingle'>> {
  return {
    content: {
      chart: {},
      title: {
        label: title,
        description: '',
        align: 'left',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      format: {
        type: 'Number',
        precision: null,
        currency: 'USD',
        dateXFormat: 'number-month-first',
        dateSeriesFormat: 'number-month-first',
      },
      data: {
        multiSeries: [{}],
        order: 'x',
        direction: 'Ascending',
        minValue: undefined,
        maxValue: undefined,
        customIcons: { enabled: false, iconMap: [] },
      },
      font: {
        style: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        lineHeight: '$t.lineHeight',
      },
    },
    style: {
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        description: { color1: '$t.value', selectorType: 'solid' },
        align: 'left',
        axis: { color1: '$t.value_a50', selectorType: 'solid' },
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingCharts' },
      },
      titleBlock: {
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      chart: {
        gridLines: { color1: '$t.outline', selectorType: 'solid' },
        gridDensity: 'regular',
        palette: { paletteName: 'palette2' },
        iconSize: '8px',
      },
      background: {
        fill: { color1: '$t.fill', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        width: '2px',
        radius: '$t.radius',
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
      alert: {
        neutral: { color1: '#f8b918', selectorType: 'solid' },
        danger: { color1: '#ef305e', selectorType: 'solid' },
        success: { color1: '#6aba4e', selectorType: 'solid' },
      },
    },
  };
}

export function getInitial(): PartialPropsOf<Exclude<ChartVariantType, 'chartSingle'>> {
  return {
    style: {
      background: {
        width: '$t.containerOutlineWidth',
      },
    },
  };
}

function isGroupByTime(model: any, target: string): boolean {
  return !!getValueByPath<DatasetColumn[]>(model, 'data.?key:value.dataSource.columns')?.some(
    (column) => column.dataType === 'Time' && column.id === target,
  );
}

//#endregion
